/**
 * SenseLab Builders — behavior layer.
 * Event delegation on document so sections keep working after
 * theme-editor re-renders. Loaded as a module (uses the theme importmap).
 */
import { StandardEvents } from '@shopify/events';

/* Sub-header cart count: mirror the theme cart after AJAX adds. */
document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  event.promise
    ?.then(({ cart, detail }) => {
      const count = cart?.totalQuantity ?? detail?.itemCount ?? 0;
      document.querySelectorAll('[data-bld-cart-count]').forEach((el) => {
        el.textContent = count < 100 ? String(count) : '99+';
        el.hidden = count === 0;
      });
    })
    .catch(() => {});
});

/* PDP gallery: thumbnail click swaps the stage image. */
function bldShowThumb(root, thumb) {
  const stageImg = root.querySelector('[data-bld-stage-img]');
  const kitBadge = root.querySelector('[data-bld-kit-badge]');

  if (stageImg && thumb.dataset.full) {
    stageImg.src = thumb.dataset.full;
    if (thumb.dataset.srcset) {
      stageImg.srcset = thumb.dataset.srcset;
    } else {
      stageImg.removeAttribute('srcset');
    }
    if (thumb.dataset.alt) stageImg.alt = thumb.dataset.alt;
  }

  if (kitBadge) kitBadge.hidden = thumb.dataset.kit !== 'true';

  root.querySelectorAll('[data-bld-thumb]').forEach((t) => {
    t.setAttribute('aria-current', t === thumb ? 'true' : 'false');
  });

  bldCenterThumb(thumb, 'smooth');
}

/* Bring the active thumb into the strip without scrolling the page. */
function bldCenterThumb(thumb, behavior) {
  const strip = thumb.closest('[data-bld-thumbs]');
  if (!strip) return;
  const s = strip.getBoundingClientRect();
  const t = thumb.getBoundingClientRect();
  strip.scrollTo({ left: strip.scrollLeft + t.left - s.left - (s.width - t.width) / 2, behavior });
}

// A ?variant= deep link can open on a thumb past the visible end of the strip.
document.querySelectorAll('[data-bld-thumbs] [aria-current="true"]').forEach((thumb) => bldCenterThumb(thumb, 'auto'));

document.addEventListener('click', (event) => {
  const thumb = event.target.closest('[data-bld-thumb]');
  const root = thumb?.closest('[data-bld-product]');
  if (root) bldShowThumb(root, thumb);
});

/* PDP quantity stepper. */
document.addEventListener('click', (event) => {
  const btn = event.target.closest('[data-bld-qty-step]');
  if (!btn) return;

  const input = btn.closest('[data-bld-qty]')?.querySelector('input[name="quantity"]');
  if (!input) return;

  const step = Number(btn.dataset.bldQtyStep) || 0;
  const next = Math.max(1, (parseInt(input.value, 10) || 1) + step);
  input.value = String(next);
});

/* PDP variant cards: sync the hidden form input, price, button state, photo and URL. */
document.addEventListener('change', (event) => {
  const option = event.target.closest('[data-bld-variant]');
  if (!option || !option.checked) return;

  const root = option.closest('[data-bld-product]');
  if (!root) return;

  const variantInput = root.querySelector('input[name="id"]');
  if (variantInput) {
    variantInput.value = option.value;
    variantInput.disabled = option.dataset.available !== 'true';
  }

  const priceEl = root.querySelector('[data-bld-price]');
  if (priceEl && option.dataset.price) priceEl.textContent = option.dataset.price;

  const atc = root.querySelector('[data-bld-atc]');
  if (atc) {
    const available = option.dataset.available === 'true';
    atc.disabled = !available;
    atc.textContent = available ? atc.dataset.labelAdd : atc.dataset.labelSoldOut;
  }

  const thumb = option.dataset.mediaId && root.querySelector(`[data-bld-thumb][data-media-id="${option.dataset.mediaId}"]`);
  if (thumb) bldShowThumb(root, thumb);

  const url = new URL(window.location.href);
  url.searchParams.set('variant', option.value);
  history.replaceState(history.state, '', url);
});

/* Builders grid carousel: arrow buttons, drag-to-scroll on desktop (touch scrolls natively). */
function bldScrollerStep(scroller) {
  const card = scroller.firstElementChild;
  if (!card) return scroller.clientWidth;
  return card.getBoundingClientRect().width + parseFloat(getComputedStyle(scroller).columnGap || '0');
}

function bldUpdateArrows(scroller) {
  const root = scroller.closest('.bld-grid');
  if (!root) return;
  const max = scroller.scrollWidth - scroller.clientWidth - 2;
  root.querySelectorAll('[data-bld-scroll]').forEach((btn) => {
    btn.disabled = Number(btn.dataset.bldScroll) < 0 ? scroller.scrollLeft <= 2 : scroller.scrollLeft >= max;
  });
}

document.addEventListener('click', (event) => {
  const btn = event.target.closest('[data-bld-scroll]');
  if (!btn) return;
  const scroller = btn.closest('.bld-grid')?.querySelector('[data-bld-scroller]');
  if (!scroller) return;
  scroller.scrollBy({ left: Number(btn.dataset.bldScroll) * bldScrollerStep(scroller), behavior: 'smooth' });
});

function bldInitScrollers() {
  document.querySelectorAll('[data-bld-scroller]:not([data-bld-ready])').forEach((scroller) => {
    scroller.dataset.bldReady = 'true';
    bldUpdateArrows(scroller);
    scroller.addEventListener('scroll', () => bldUpdateArrows(scroller), { passive: true });

    let down = false;
    let moved = false;
    let startX = 0;
    let startScroll = 0;

    scroller.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'mouse') return;
      down = true;
      moved = false;
      startX = event.clientX;
      startScroll = scroller.scrollLeft;
    });

    scroller.addEventListener('pointermove', (event) => {
      if (!down) return;
      const dx = event.clientX - startX;
      if (!moved && Math.abs(dx) > 4) {
        moved = true;
        scroller.classList.add('is-dragging');
      }
      if (moved) scroller.scrollLeft = startScroll - dx;
    });

    const end = () => {
      if (!down) return;
      down = false;
      scroller.classList.remove('is-dragging');
    };
    scroller.addEventListener('pointerup', end);
    scroller.addEventListener('pointerleave', end);

    // Swallow the click that ends a drag so cards and "+ Sacola" don't fire.
    scroller.addEventListener(
      'click',
      (event) => {
        if (!moved) return;
        event.preventDefault();
        event.stopPropagation();
        moved = false;
      },
      true
    );
    scroller.addEventListener('dragstart', (event) => event.preventDefault());
  });
}

bldInitScrollers();
document.addEventListener('shopify:section:load', bldInitScrollers);
